import { SidePanelPageLayoutTsoPq } from '@components/Grid/GridTsoPq';
import { SidePanelTsoPq } from '@components/Grid/SidePanelTsoPq';

import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ImagePlaceholderTsoPq from '@components/ImagePlaceholder/ImagePlaceholderTsoPq';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import { downloadExcelFile } from '@pages/MeasurementVisualization/utils';
import {
  PostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesApiResponse,
  usePostHarmonicBackgroundLevelExportHarmonicBackgroundLevelValuesMutation,
} from '@redux/apiSlices/harmonicBackgroundLevelApi';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import { useAppSelector } from '@redux/hooks';
import {
  selectDataToBeDisplayedInChart,
  selectDeviceTechnicalNames,
  selectIsLoading,
  selectSelectedHarmonicsIds,
} from '@redux/slices/harmonicBackgroundLevelSlice';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { HarmonicBackgroundLevelBoxplot } from './HarmonicBackgroundLevelBoxplot';
import { SortHarmonicDataByOrderThenPhase } from './harmonicBackgroundLevelHelpers';
import HarmonicBackgroundLevelParameters from './HarmonicBackgroundLevelParameters';
import {
  StyledContent,
  StyledContentTitle,
  StyledContentWrapper,
  StyledHeaderContainer,
  StyledIconButtonTsoPq,
} from './harmonicBackgroundLevelStyles';

export const HarmonicBackgroundLevel = () => {
  const { t } = useTranslation();
  const dataToBeDisplayedInChart = useSelector(selectDataToBeDisplayedInChart);
  const selectedHarmonics = useSelector(selectSelectedHarmonicsIds)
  const isLoading = useAppSelector(selectIsLoading);
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const technicalNames = useAppSelector(selectDeviceTechnicalNames);
  const [triggerExport] = usePostHarmonicBackgroundLevelExportHarmonicBackgroundLevelValuesMutation();

  const getOrderLabel = (harmonicId: number | undefined): string => {
    if (!harmonicId || !measurementParameters?.externalDtos) return '-';

    const param = measurementParameters.externalDtos.find((par) => par.id === harmonicId);
    if (!param) return '-';

    const orderMatch = param.subcategory?.match(/order(\d+)/i);
    return orderMatch ? orderMatch[1] : '-';
  };

  const processHarmonicDto = (dto: { measurementParameterId?: number; data?: number[][] }, xIndex: number) => {
    const adaptedData: number[][] = [];
    const categories: string[] = [];
    const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];

    if (!dto.data) return { adaptedData, categories, plotBands, xIndex };

    dto.data.forEach((phaseArray) => {
      adaptedData.push(phaseArray);
      categories.push(getOrderLabel(dto.measurementParameterId));
    });

    plotBands.push({
      from: xIndex - 0.5,
      to: xIndex + dto.data.length - 0.5,
      color: tsoPqTheme.palette.background.other.color_other_background_white,
    });

    return { adaptedData, categories, plotBands, xIndex: xIndex + dto.data.length };
  };

  const transformHarmonicData = (
    dataFromBE: PostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesApiResponse
  ) => {
    let xIndex = 0;
    const adaptedData: number[][] = [];
    const categories: string[] = [];
    const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];

    const sortedDtos = SortHarmonicDataByOrderThenPhase(dataFromBE, measurementParameters!);

    sortedDtos.forEach((dto) => {
      // @ts-ignore
      const result = processHarmonicDto(dto, xIndex);
      adaptedData.push(...result.adaptedData);
      categories.push(...result.categories);
      plotBands.push(...result.plotBands);
      xIndex = result.xIndex;
    });

    return { adaptedData, categories, plotBands };
  };

  const renderDeviceChart = (device: any) => {
    const { adaptedData, categories, plotBands } = transformHarmonicData(device.harmonics ?? []);
    return (
      <HarmonicBackgroundLevelBoxplot
        key={`${device.tsoId}-${device.measurementDeviceId}`}
        plotBands={plotBands}
        adaptedData={adaptedData}
        categories={categories}
        tsoId={device.tsoId}
        measurementDeviceLabel={device.deviceLabel ?? '-'}
      />
    );
  };

  const renderBoxplot = () => {
    if (isLoading) {
      return <LoadingSpinnerTsoPq />;
    }

    if (dataToBeDisplayedInChart.length === 0) {
      return (
        <ImagePlaceholderTsoPq
          title={t('measurementExport.parameters.placeholder.title')}
          description={t('harmonicBackgroundLevel.placeholderDescripttion')}
        />
      );
    }

    return <>{dataToBeDisplayedInChart.map(renderDeviceChart)}</>;
  };

  const handleExportHarmonicBackgroundLevels = () => {
    dataToBeDisplayedInChart.map(async (deviceData, index) => {
      if (deviceData.start && deviceData.end && technicalNames) {
        try {
          const response = await triggerExport({
            from: deviceData.start,
            to: deviceData.end,
            measurementDeviceTechnicalName: deviceData.technicalName,
            tsoId: deviceData.tsoId,
            body: selectedHarmonics,
          }).unwrap();

          downloadExcelFile(response as Blob, deviceData.technicalName, deviceData.start, deviceData.end);
        } catch (error) {
          console.error(error);
        }
      }
    });
  };

  return (
    <SidePanelPageLayoutTsoPq
      sidePanel={
        <SidePanelTsoPq
          sectionTitle={t('importedMeasurementOverview.sectionTitle')}
          firstSlot={<HarmonicBackgroundLevelParameters />}
        />
      }
      content={
        <StyledContentWrapper>
          <StyledContent>
            <StyledHeaderContainer>
              <StyledContentTitle>
                <TypographyTsoPq typographyType="pageTitleBold">
                  {t('harmonicsBackgroundLevel.pageTitle')}
                </TypographyTsoPq>
                <TypographyTsoPq typographyType="pageSubtitleSM">
                  {t('harmonicsBackgroundLevel.pageSubtitle')}
                </TypographyTsoPq>
              </StyledContentTitle>
              {dataToBeDisplayedInChart.length > 0 && (
                <StyledIconButtonTsoPq
                  sizeVariant="m"
                  buttonType="outlinedPrimary"
                  icon={faFileArrowDown}
                  onClick={() => handleExportHarmonicBackgroundLevels()}
                  tooltip={t('general.tooltip.exportAll')}
                />
              )}
            </StyledHeaderContainer>
            {renderBoxplot()}
          </StyledContent>
        </StyledContentWrapper>
      }
    />
  );
};
